import { faroCoreApiClient } from "@api/faro-core-api-client";
import { AdmPlan, SubjectTypeProps } from "@custom-types/base-types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "@store/store-helper";
import { FaroCoreApiConst, FaroCoreApiTypes } from "@stellar/core-api-client";

interface FetchAdmPlansProps extends SubjectTypeProps {
  /** The ID of the subject entity that the plans are related to */
  subjectId: string;
}

/** Fetch the plans of a subjectType */
export const fetchPlans = createAsyncThunk<AdmPlan[], FetchAdmPlansProps>(
  "plans/fetchPlans",
  async ({ subjectType, subjectId }) => {
    switch (subjectType) {
      case "COMPANY":
      case "DEVICE":
        return await faroCoreApiClient.admin.getCompanyPlansById({
          companyId: subjectId,
        });
      case "PROJECT":
        return await faroCoreApiClient.admin.getProjectPlansById({
          projectId: subjectId,
        });
      case "USER":
        return await faroCoreApiClient.admin.getMemberPlansById({
          memberId: subjectId,
        });
      default:
        throw new Error(`Unsupported subject type: ${subjectType}`);
    }
  }
);

/** Props for deactivating a plan */
interface DeactivatingPlanProps {
  /* The type of plan update, in this case deactivating the plan */
  type: "deactivating";

  /** The comment to be added to the plan's description after deactivation */
  deactivatedComment: string;
}

/** Props for updating a plan */
interface UpdatingPlanProps {
  /* The type of plan update, in this case updating the plan */
  type: "updating";

  /** The comment to be added to the plan's description after updating */
  updatedComment: string;
}

/** Props for changing the end date of a plan */
interface UpdatingPlanEndDateProps {
  /* The type of plan update, in this case changing the end date of the plan */
  type: "updatingEndDate";

  /** The new end date of the plan */
  endDate: FaroCoreApiTypes.Iso8601Date;
}

interface UpdatePlanProps {
  /** The id of the plan to be updated */
  planId: AdmPlan["id"];

  /** Changing parameters for the plan */
  updatedProps:
    DeactivatingPlanProps | UpdatingPlanProps | UpdatingPlanEndDateProps;
}

/** Update a plan's details. This can be updating a comment or for example deactivating the plan */
export const updatePlan = createAsyncThunk<
  AdmPlan,
  UpdatePlanProps,
  { state: RootState }
>("plans/updatePlan", async ({ planId, updatedProps }, { getState }) => {
  const { plans } = getState();
  const plan = plans.entities[planId];

  if (!plan) {
    throw new Error(`Plan with id ${planId} not found`);
  }

  if (updatedProps.type === "updatingEndDate") {
    return await faroCoreApiClient.admin.updatePlan({
      planId: plan.id,
      updatedPlan: { endDate: updatedProps.endDate },
    });
  }

  return await faroCoreApiClient.admin.updatePlan({
    planId: plan.id,
    updatedPlan: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      deactivated: updatedProps.type === "deactivating",
      description:
        updatedProps.type === "deactivating"
          ? updatedProps.deactivatedComment
          : updatedProps.updatedComment,
    },
  });
});

interface DeletePlanProps {
  /** The plan that should be deleted */
  planId: AdmPlan["id"];
}

/**
 * Permanently deletes a plan. Unlike a deactivation, the plan is gone afterwards,
 * so it is also removed from the list of plans.
 */
export const deletePlan = createAsyncThunk<AdmPlan, DeletePlanProps>(
  "plans/deletePlan",
  async ({ planId }) => await faroCoreApiClient.admin.deletePlan({ planId })
);

interface UnassignSceneSeatProps {
  /** The SCENE seat that should become free again */
  plan: AdmPlan;
}

/**
 * Frees an assigned SCENE seat again by handing it back to the system user, which is how the
 * backend models a seat that nobody holds. The member keeps their workspace membership, they
 * only lose the access to SCENE.
 *
 * A seat is a subscription of the workspace paying for it, so it is freed through that workspace
 * and its plans are refetched afterwards, to show the seat as available again.
 */
export const unassignSceneSeat = createAsyncThunk<
  void,
  UnassignSceneSeatProps,
  { state: RootState }
>("plans/unassignSceneSeat", async ({ plan }, { dispatch }) => {
  if (plan.subscriber.type !== "COMPANY") {
    throw new Error("Only the SCENE seats of a workspace can be unassigned.");
  }

  const companyId = plan.subscriber.id;

  await faroCoreApiClient.company.assignMemberToSubscription({
    companyId,
    subscriptionId: plan.id,
    payload: { memberIdentifier: FaroCoreApiConst.SYSTEM_USER_ID },
  });

  await dispatch(
    fetchPlans({ subjectType: "COMPANY", subjectId: companyId })
  ).unwrap();
});
